import { ReactComponent as TrashIcon } from '../../../../images/trash.svg';
import '../RecommendationTableWidget.scss';
import { store } from '../../../../store';
import type { Asset } from '../../../../features/navigation/AssetGroupsSlice';
import { formatNumber } from '../../../../utilities/Helpers';
import { formatDate } from '../../../../utilities/dateHelpers';

// Helper: find the asset (well) object by its display name from assetGroups slice
export const findAssetByWellName = (wellName: string): Asset | undefined => {
  if (!wellName) return undefined;
  const assets = store.getState().assetGroups.assets ?? [];
  const lower = wellName.toLowerCase();
  return assets.find((a) => a.assetName?.toLowerCase() === lower);
};

export const handleWellFieldClick = (wellName: string, source: 'wellname' | 'testdate') => {
  const baseUrl = window.location.origin;
  const asset = findAssetByWellName(wellName);
  if (!asset?.assetId) return;

  if (source === 'wellname') {
    if (!asset.groupName) return;
    const targetUrl = `${baseUrl}/asset/charts/${asset.assetId}/${asset.groupName}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  } else {
    const targetUrl = `${baseUrl}/pumpchecker/ESPDiagnosis/${asset.assetId}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }
};

// Generic function to create table columns from MongoDB data
const createTableColumns = (
  columnHeaders: any[],
  fixedColumns: any[] = [],
  additionalColumns: any[] = [],
  widgetKey = '',
): any[] => {
  const baseColumns = [...additionalColumns];

  let groupedHeaders: Record<string, { columns: string[]; header: string }> = {};

  switch (widgetKey) {
    case 'espRecommendationsFrequencyChange':
      groupedHeaders = {
        lastTestInfo: {
          header: 'Last Test',
          columns: ['DateString', 'Flowrate', 'WaterCut', 'Oil', 'PumpIntakePressure'],
        },
        wellInflow: {
          header: 'Well Inflow',
          columns: ['ReservoirPressure', 'ProductivityIndex'],
        },
      };
      break;

    case 'espRecommendationsInjectionRateIncrease':
    case 'glRecommendationsInjectionRate':
      groupedHeaders = {
        lastTestInfo: {
          header: 'Last Test',
          columns: ['DateString', 'Liquid', 'WaterCut', 'Oil'],
        },
        wellInflow: {
          header: 'Well Inflow',
          columns: ['ReservoirPressure', 'ResultProductivityIndex'],
        },
      };
      break;

    case 'espRecommendationsPumpChange':
      groupedHeaders = {
        lastTestInfo: {
          header: 'Last Test',
          columns: ['DateString', 'Flowrate', 'WaterCut', 'TotalGasOilRatio'],
        },
        wellInflow: {
          header: 'Well Inflow',
          columns: ['ReservoirPressure', 'ProductivityIndex'],
        },
      };
      break;

    case 'glRecommendationsInjectionRateReduction':
      groupedHeaders = {
        lastTestInfo: {
          header: 'Last Test',
          columns: ['DateString', 'Liquid', 'WaterCut', 'Oil'],
        },
        wellInflow: {
          header: 'Well Inflow',
          columns: ['ReservoirPressure', 'ResultProductivityIndex'],
        },
      };
      break;

    case 'glRecommendationsWHPReduction':
      groupedHeaders = {
        lastTestInfo: {
          header: 'Last Test',
          columns: ['DateString', 'InjectionRate', 'Liquid', 'WaterCut', 'Oil', 'GOR'],
        },
      };
      break;

    case 'glRecommendationsDepthChange':
      groupedHeaders = {
        valveDesignInfo: {
          header: 'Valve Design Info',
          columns: ['InjectionDepthLikely', 'InjectionDepthMaximum'],
        },
        lastTestInfo: {
          header: 'Last Test',
          columns: ['DateString', 'Liquid', 'WaterCut', 'Oil', 'InjectionRate'],
        },
      };
      break;

    default:
      groupedHeaders = {};
      break;
  }

  // fixed columns first
  const processedFixedColumns = fixedColumns
    .filter((col: any) => col.label)
    .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
    .map((col: any) => {
      return createColumnFromConfig(col, widgetKey);
    });

  baseColumns.push(...processedFixedColumns);

  // columns from MongoDB and sort by order
  const selectedColumns = columnHeaders
    .filter((col: any) => col.selected)
    .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

  // Track which columns have been processed to avoid duplicates
  const processedColumnKeys = new Set();

  selectedColumns.forEach((col: any) => {
    if (processedColumnKeys.has(col.key)) return;

    let isGrouped = false;

    // Check if this column belongs to a group
    for (const [, groupInfo] of Object.entries(groupedHeaders)) {
      if (groupInfo.columns.includes(col.key)) {
        const groupColumns = selectedColumns
          .filter((c) => groupInfo.columns.includes(c.key) && c.selected && !processedColumnKeys.has(c.key))
          .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

        if (groupColumns.length > 0) {
          groupColumns.forEach((gc) => processedColumnKeys.add(gc.key));

          baseColumns.push({
            Header: groupInfo.header,
            columns: groupColumns.map((c: any) => createColumnFromConfig(c, widgetKey)),
          });
        }

        isGrouped = true;
        break;
      }
    }

    // If not grouped, add as individual column
    if (!isGrouped && !processedColumnKeys.has(col.key)) {
      baseColumns.push(createColumnFromConfig(col, widgetKey));
      processedColumnKeys.add(col.key);
    }
  });

  return baseColumns;
};

// Create individual column from config
const createColumnFromConfig = (col: any, widgetKey = ''): any => {
  const baseColumn: any = {
    Header: col.label,
    accessor: col.key || col.label,
    id: col.key || col.label,
  };

  // Handle special cell renderers based on column key
  if (
    ['InstallDate', 'StartDate', 'FailDate', 'PullDate', 'DateString', 'BeforeDateString', 'AfterDateString'].includes(
      col.key,
    )
  ) {
    baseColumn.Cell = ({ value, row }: { value: any; row: any }) => {
      const formatDateValue = (dateValue: any) => {
        if (!dateValue) return dateValue;
        try {
          const date = new Date(dateValue);
          if (isNaN(date.getTime())) return dateValue;
          return formatDate(date);
        } catch {
          return dateValue;
        }
      };

      if (['DateString', 'BeforeDateString', 'AfterDateString'].includes(col.key)) {
        const wellName = row.original.WellName;
        const formattedValue = formatDateValue(value);

        if (col.key === 'DateString' && value) {
          const parsedDate = new Date(value);
          const today = new Date();
          const diffDays = Math.floor((today.getTime() - parsedDate.getTime()) / (1000 * 60 * 60 * 24));
          return (
            <a
              className={`table-row-link well-name ${diffDays > 60 ? 'test-date-validation' : ''}`}
              onClick={() => handleWellFieldClick(wellName, 'testdate')}
            >
              {formattedValue}
            </a>
          );
        }

        return (
          <a className='table-row-link well-name' onClick={() => handleWellFieldClick(wellName, 'testdate')}>
            {formattedValue}
          </a>
        );
      }
      const formattedValue = formatDateValue(value);
      return <span>{formattedValue}</span>;
    };
  } else if (col.key === 'WellName') {
    baseColumn.Cell = ({ value }: { value: any }) => (
      <a className='table-row-link well-name' onClick={() => handleWellFieldClick(value, 'wellname')}>
        {value}
      </a>
    );
  } else if (col.label === 'Task' && (!col.key || col.key === '')) {
    // Handle Task column from fixedColumns
    baseColumn.width = 80;
    baseColumn.disableSortBy = true;
    baseColumn.accessor = () => 'task';
    baseColumn.disableFilters = true;
    baseColumn.Cell = ({ row }: { row: any }) => {
      const handleClick = () => {
        const baseUrl = window.location.origin;
        // Determine target URL
        const isESPWidget = 'Frequency' in row.original || 'NewFrequency' in row.original;
        const targetUrl = isESPWidget
          ? `${baseUrl}/pumpchecker/Checker/Well/Recommendations`
          : `${baseUrl}/pumpchecker/Checker/GasLiftReport/Recommendations`;
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
      };

      return (
        <button className='recommend-button' onClick={handleClick}>
          Recommend
        </button>
      );
    };
  } else if (
    ['NotFeasibleFrequencyChangeString', 'NotFeasibleInjectionIncrease', 'NotFeasibleFrequencyChange'].includes(col.key)
  ) {
    baseColumn.Cell = ({ value }: { value: any }) => (
      <div className='recommendation-table-checkbox'>
        <input type='checkbox' checked={value === 'Yes' || !!value} disabled className='card-table-checkbox' />
      </div>
    );
    baseColumn.disableFilters = true;
  } else if (col.key === 'Status') {
    baseColumn.Cell = ({ value }: { value: any }) => {
      const getStatusClass = (status: string) => {
        const lowerStatus = status?.toLowerCase() || '';
        if (lowerStatus.includes('pending')) return 'pending';
        if (lowerStatus.includes('tracked') && !lowerStatus.includes('no longer')) return 'tracked';
        if (lowerStatus.includes('no longer tracked')) return 'no-longer-tracked';
        return '';
      };

      const statusClass = getStatusClass(value);

      return (
        <div className='recommended-status-indicator'>
          {statusClass && <div className={`status-circle ${statusClass}`} />}
        </div>
      );
    };
    baseColumn.disableFilters = true;
  } else if (col.key === 'DisplayEquipment' || col.key === 'Equipment') {
    baseColumn.accessor = (row: any) => {
      // Removed console.log to avoid improper output neutralization for logs (CWE-117)
      return row.Equipment?.DisplayEquipment || row.EspEquipment?.DisplayEquipment || '';
    };
    baseColumn.Cell = ({ value }: { value: any }) => {
      return (
        <div className='equipment-cell' title={value}>
          {value}
        </div>
      );
    };
  } else if (col.key && isNumericField(col.key)) {
    // Get decimal places for specific fields

    const decimals = getDecimals(col.key);
    const shouldHighlight = shouldHighlightField(col.key, widgetKey);
    const isRedText =
      (col.key === 'InjectionRate' || col.key === 'CurrentWHP') &&
      !(
        col.key === 'InjectionRate' &&
        (widgetKey === 'glRecommendationsWHPReduction' || widgetKey === 'glRecommendationsDepthChange')
      );

    baseColumn.Cell = ({ value }: { value: any }) => {
      const formattedValue = formatNumber(value, decimals);

      if (shouldHighlight) {
        const isFirstHighlightField =
          col.key === 'InjectionRate' &&
          !(widgetKey === 'glRecommendationsWHPReduction' || widgetKey === 'glRecommendationsDepthChange');
        const className =
          `highlight-cell ${isRedText ? 'red-text' : ''} ${isFirstHighlightField ? 'first-highlights' : ''}`.trim();

        return (
          <div className={className}>
            <span>{formattedValue}</span>
          </div>
        );
      }

      return <span>{formattedValue}</span>;
    };
  } else {
    baseColumn.Cell = ({ value }: { value: any }) => {
      if (value === null || value === undefined) return <span>-</span>;
      if (typeof value === 'object') return <span>{JSON.stringify(value)}</span>;
      return <span>{value}</span>;
    };
  }

  return baseColumn;
};

// Helper function for field styling
export const isNumericField = (key: string): boolean => {
  const numericFields = [
    'Frequency',
    'NewFrequency',
    'FrequencyChangeAdditionalOil',
    'IncrementalChange',
    'IncrementalGain',
    'RunlifeDays',
    'MotorLoadPercentage',
    'DriveLoadPercentage',
    'MotorTemperature',
    'Flowrate',
    'Liquid',
    'WaterCut',
    'Oil',
    'PumpIntakePressure',
    'ReservoirPressure',
    'ProductivityIndex',
    'ResultProductivityIndex',
    'CumulativeGain',
    'BeforeControlValue',
    'BeforeOil',
    'AdditionalOil',
    'NominalRate',
    'PumpChangePwf',
    'Degradation',
    'TotalGasOilRatio',
    'InjectionRate',
    'AdditionalOilInjectedGasRateRequired',
    'AdditionalOilInjectedGasRateIncrease',
    'NewGrossRate',
    'ReducedOilInjectedGasRateRequired',
    'ReducedOilInjectedGasRateDecrease',
    'NewGrossRateReduced',
    'CurrentWHP',
    'AdditionalOilWHPRequired',
    'AdditionalOilWHPDecrease',
    'NewGrossRateAtReducedWHP',
    'InjectionDepthLikely',
    'InjectionDepthMaximum',
    'AdditionalOilDepthIncrease',
    'GOR',
    'GrossRate',
    'FrequencyChangeAdditionalWater',
    'FormattedBeforeControlValue',
  ];
  return numericFields.includes(key);
};

export const getDecimals = (key: string): number => {
  const decimalMap: Record<string, number> = {
    WaterCut: 1,
    MotorTemperature: 1,
    PumpIntakePressure: 1,
    ReservoirPressure: 1,
    ProductivityIndex: 4,
    ResultProductivityIndex: 4,
    CumulativeGain: 1,
    NominalRate: 1,
    PumpChangePwf: 1,
    Degradation: 1,
    NewGrossRate: 2,
    NewGrossRateReduced: 2,
    NewGrossRateAtReducedWHP: 2,
    FrequencyChangeAdditionalWater: 2,
  };
  return decimalMap[key] || 0;
};

const shouldHighlightField = (key: string, widgetKey = ''): boolean => {
  if (
    key === 'InjectionRate' &&
    (widgetKey === 'glRecommendationsWHPReduction' || widgetKey === 'glRecommendationsDepthChange')
  ) {
    return false;
  }

  const highlightFields = [
    'Frequency',
    'NewFrequency',
    'FrequencyChangeAdditionalOil',
    'IncrementalChange',
    'IncrementalGain',
    'InjectionRate',
    'AdditionalOilInjectedGasRateRequired',
    'AdditionalOilInjectedGasRateIncrease',
    'ReducedOilInjectedGasRateRequired',
    'ReducedOilInjectedGasRateDecrease',
    'CurrentWHP',
    'AdditionalOilWHPRequired',
    'AdditionalOilWHPDecrease',
  ];
  return highlightFields.includes(key);
};

const WIDGET_CONFIG = {
  espRecommendationsFrequencyChange: {
    key: 'espRecommendationsFrequencyChange',
    additionalColumns: [],
  },
  espRecommendationsTrackedFrequency: {
    key: 'espRecommendationsTrackedFrequency',
    additionalColumns: [
      {
        Header: '',
        id: 'delete-actions',
        width: 80,
        Cell: () => (
          <div className='tdt-action-cell'>
            <TrashIcon style={{ opacity: 0.4, cursor: 'not-allowed' }} />
          </div>
        ),
      },
    ],
  },
  espRecommendationsPumpChange: {
    key: 'espRecommendationsPumpChange',
    additionalColumns: [],
  },
  glRecommendationsInjectionRate: {
    key: 'glRecommendationsInjectionRate',
    additionalColumns: [],
  },
  glRecommendationsTrackedInjectedGasRate: {
    key: 'glRecommendationsTrackedInjectedGasRate',
    additionalColumns: [
      {
        Header: '',
        id: 'delete-actions',
        width: 80,
        Cell: () => (
          <div className='tdt-action-cell'>
            <TrashIcon style={{ opacity: 0.4, cursor: 'not-allowed' }} />
          </div>
        ),
      },
    ],
  },
  glRecommendationsInjectionRateReduction: {
    key: 'glRecommendationsInjectionRateReduction',
    additionalColumns: [],
  },
  glRecommendationsWHPReduction: {
    key: 'glRecommendationsWHPReduction',
    additionalColumns: [],
  },
  glRecommendationsDepthChange: {
    key: 'glRecommendationsDepthChange',
    additionalColumns: [],
  },
} as const;

const createColumnsFromCurrentState = (widgetKey: keyof typeof WIDGET_CONFIG) => {
  const state = store.getState();
  const recommendationsPageData = state.testWellColumnsSlice.data?.widgets;
  const recommendationsData = recommendationsPageData?.find((obj: any) => obj?.key === widgetKey) || null;
  const columnHeaders = recommendationsData?.widgetProperty?.widgetProperties?.columns || [];
  const fixedColumns = recommendationsData?.widgetProperty?.widgetProperties?.fixedColumns || [];
  const config = WIDGET_CONFIG[widgetKey];
  return createTableColumns(columnHeaders, fixedColumns, [...config.additionalColumns], widgetKey);
};

export const frequencyRateTableColumns = () => createColumnsFromCurrentState('espRecommendationsFrequencyChange');
export const trackedFrequencyRateTableColumns = () =>
  createColumnsFromCurrentState('espRecommendationsTrackedFrequency');
export const pumpChangeTableColumns = () => createColumnsFromCurrentState('espRecommendationsPumpChange');
export const injectRateIncreaseTableColumns = () => createColumnsFromCurrentState('glRecommendationsInjectionRate');
export const trackedInjectionGasRateTableColumns = () =>
  createColumnsFromCurrentState('glRecommendationsTrackedInjectedGasRate');
export const injectionRateReductionTableColumns = () =>
  createColumnsFromCurrentState('glRecommendationsInjectionRateReduction');
export const whpReductionTableColumns = () => createColumnsFromCurrentState('glRecommendationsWHPReduction');
export const depthChangeTableColumns = () => createColumnsFromCurrentState('glRecommendationsDepthChange');
